import { useEffect, useRef, useState } from "react";

/**
 * Lightweight IntersectionObserver hook. Returns a ref to attach to the
 * element and a boolean that flips to true the first time the element enters
 * the viewport. We use `once: true` semantics so heavy sections fetch exactly
 * once when revealed and never re-trigger on scroll.
 *
 * Falls back to immediately-visible when IntersectionObserver is unavailable
 * (older browsers / jsdom) so nothing silently stays blank.
 */
export function useInView({ rootMargin = "200px", once = true } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin, once]);

  return [ref, inView];
}
